(() => {
  const componentName = 'product-video-tutorials';

  if (customElements.get(componentName)) return;

  class ProductVideoTutorials extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.sectionRoot = this.closest('product-info');
      this.track = this.querySelector('[data-video-track]');
      this.previousButton = this.querySelector('[data-video-prev]');
      this.nextButton = this.querySelector('[data-video-next]');
      this.modal = this.querySelector('[data-video-modal]');
      this.player = this.querySelector('[data-video-player]');
      this.modalTitle = this.querySelector('[data-video-modal-title]');
      this.closeButton = this.querySelector('.bt-product-videos__close');
      this.triggers = Array.from(this.querySelectorAll('[data-video-trigger]'));
      this.closeControls = Array.from(this.querySelectorAll('[data-video-close]'));

      this.marker = document.createComment(` ${componentName} mobile position `);
      this.parentNode.insertBefore(this.marker, this);
      this.mediaQuery = window.matchMedia('(min-width: 768px)');

      this.onMediaChange = this.placeComponent.bind(this);
      this.onPrevious = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
      this.onTrackScroll = this.queueArrowUpdate.bind(this);
      this.onTrackKeydown = this.handleTrackKeydown.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.onTriggerClick = this.handleTriggerClick.bind(this);
      this.onCloseClick = this.closeModal.bind(this);

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track?.addEventListener('scroll', this.onTrackScroll, { passive: true });
      this.track?.addEventListener('keydown', this.onTrackKeydown);
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTriggerClick));
      this.closeControls.forEach((control) => control.addEventListener('click', this.onCloseClick));
      document.addEventListener('keydown', this.onDocumentKeydown);

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      } else {
        this.mediaQuery.addListener(this.onMediaChange);
      }

      if ('ResizeObserver' in window && this.track) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      } else {
        this.onWindowResize = this.updateArrows.bind(this);
        window.addEventListener('resize', this.onWindowResize);
      }

      this.placeComponent();
      this.hydrateYouTubeThumbnails();
      this.removeAttribute('data-unmounted');
      requestAnimationFrame(() => this.updateArrows());
    }

    disconnectedCallback() {
      requestAnimationFrame(() => {
        if (!this.isConnected) this.destroy();
      });
    }

    placeComponent() {
      if (!this.sectionRoot || !this.marker?.isConnected) return;

      if (!this.mediaQuery.matches) {
        if (this.marker.nextSibling !== this) this.marker.after(this);
        return;
      }

      const mediaWrappers = Array.from(
        this.sectionRoot.querySelectorAll('.product.comp-product > .product__media-wrapper')
      );

      let desktopTarget;
      if (this.dataset.mediaPosition === 'right') {
        desktopTarget = mediaWrappers.find((wrapper) => wrapper.classList.contains('small-hide'));
      } else {
        desktopTarget = mediaWrappers.find((wrapper) => wrapper.classList.contains('wbproleftimg'));
        desktopTarget = desktopTarget?.querySelector(':scope > .product__column-sticky') || desktopTarget;
      }

      desktopTarget = desktopTarget || mediaWrappers[0];
      if (desktopTarget && this.parentNode !== desktopTarget) desktopTarget.appendChild(this);
    }

    scrollByCard(direction) {
      if (!this.track) return;

      const firstCard = this.track.querySelector('.bt-product-videos__card');
      if (!firstCard) return;

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = firstCard.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    handleTrackKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollByCard(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollByCard(1);
      }
    }

    queueArrowUpdate() {
      if (this.arrowFrame) return;

      this.arrowFrame = requestAnimationFrame(() => {
        this.updateArrows();
        this.arrowFrame = null;
      });
    }

    updateArrows() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const currentScroll = Math.max(0, this.track.scrollLeft);
      const hasOverflow = maximumScroll > 2;

      this.classList.toggle('bt-product-videos--no-overflow', !hasOverflow);
      this.previousButton.disabled = !hasOverflow || currentScroll <= 2;
      this.nextButton.disabled = !hasOverflow || currentScroll >= maximumScroll - 2;
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      this.openModal(
        trigger.dataset.videoUrl,
        trigger.dataset.videoTitle || 'Tutorial video',
        trigger.dataset.videoPoster || '',
        trigger
      );
    }

    openModal(url, title, poster, trigger) {
      if (!this.modal || !this.player) return;

      const media = this.createPlayer(url, title, poster);
      if (!media) return;

      this.lastFocusedElement = trigger;
      if (this.modal.parentNode !== document.body) document.body.appendChild(this.modal);
      this.player.replaceChildren(media);
      if (this.modalTitle) this.modalTitle.textContent = title;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bt-video-modal-open');
      this.closeButton?.focus({ preventScroll: true });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.player?.replaceChildren();
      document.documentElement.classList.remove('bt-video-modal-open');
      this.lastFocusedElement?.focus({ preventScroll: true });
      this.lastFocusedElement = null;
      if (this.modal.parentNode !== this) this.appendChild(this.modal);
    }

    handleDocumentKeydown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.modal.querySelectorAll('button:not([disabled]), a[href], iframe, video, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    createPlayer(rawUrl, title, poster) {
      const video = this.parseVideoUrl(rawUrl);
      if (!video) return null;

      if (video.type === 'youtube' || video.type === 'vimeo') {
        const iframe = document.createElement('iframe');
        iframe.src = video.embedUrl;
        iframe.title = title;
        iframe.loading = 'lazy';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allowFullscreen = true;
        return iframe;
      }

      if (video.type === 'file') {
        const player = document.createElement('video');
        player.src = video.url;
        player.controls = true;
        player.autoplay = true;
        player.playsInline = true;
        player.preload = 'metadata';
        if (poster) player.poster = poster;
        return player;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'bt-product-videos__external-link';
      const link = document.createElement('a');
      link.href = video.url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = 'Deschide videoclipul intr-o fila noua';
      wrapper.appendChild(link);
      return wrapper;
    }

    parseVideoUrl(rawUrl) {
      let url;

      try {
        url = new URL(rawUrl, window.location.origin);
      } catch (error) {
        return null;
      }

      if (!['http:', 'https:'].includes(url.protocol)) return null;

      const host = url.hostname.toLowerCase().replace(/^www\./, '');
      const pathParts = url.pathname.split('/').filter(Boolean);
      let videoId = '';

      if (host === 'youtu.be') {
        videoId = pathParts[0] || '';
      } else if (
        host === 'youtube.com' ||
        host === 'm.youtube.com' ||
        host === 'music.youtube.com' ||
        host === 'youtube-nocookie.com'
      ) {
        if (url.pathname === '/watch') {
          videoId = url.searchParams.get('v') || '';
        } else if (['embed', 'shorts', 'live'].includes(pathParts[0])) {
          videoId = pathParts[1] || '';
        }
      }

      videoId = videoId.replace(/[^a-zA-Z0-9_-]/g, '');
      if (videoId) {
        return {
          type: 'youtube',
          id: videoId,
          url: url.href,
          embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`,
        };
      }

      if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
        const vimeoId = pathParts.find((part) => /^\d+$/.test(part));
        if (vimeoId) {
          return {
            type: 'vimeo',
            id: vimeoId,
            url: url.href,
            embedUrl: `https://player.vimeo.com/video/${vimeoId}?autoplay=1`,
          };
        }
      }

      if (/\.(mp4|webm|ogg)$/i.test(url.pathname)) {
        return { type: 'file', url: url.href };
      }

      return { type: 'external', url: url.href };
    }

    hydrateYouTubeThumbnails() {
      this.triggers.forEach((trigger) => {
        const placeholder = trigger.querySelector('[data-video-placeholder]');
        if (!placeholder) return;

        const parsed = this.parseVideoUrl(trigger.dataset.videoUrl);
        if (!parsed || parsed.type !== 'youtube') return;

        const image = document.createElement('img');
        image.className = 'bt-product-videos__image';
        image.src = `https://i.ytimg.com/vi/${parsed.id}/hqdefault.jpg`;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        placeholder.replaceWith(image);
      });
    }

    destroy() {
      this.closeModal();
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onTrackScroll);
      this.track?.removeEventListener('keydown', this.onTrackKeydown);
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this.onTriggerClick));
      this.closeControls?.forEach((control) => control.removeEventListener('click', this.onCloseClick));
      document.removeEventListener('keydown', this.onDocumentKeydown);

      if (this.mediaQuery?.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onMediaChange);
      } else {
        this.mediaQuery?.removeListener(this.onMediaChange);
      }

      this.resizeObserver?.disconnect();
      if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
      if (this.arrowFrame) cancelAnimationFrame(this.arrowFrame);
      this.initialized = false;
    }
  }

  customElements.define(componentName, ProductVideoTutorials);
})();
